import * as React from 'react';

import {
    type CustomizationContentLink,
    type CustomizationHeaderItem,
    type Site,
    SiteInsightsLinkPosition,
} from '@gitbook/api-client';
import { createPalette, PaletteButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { ChevronDown } from '@gitbook/spine-icons/src/icons';
import {
    ActionableContainer,
    Actions,
    Button,
    ButtonSkeleton,
    Collection,
    Description,
    DialogRoot,
    DialogTrigger,
    DragHandle,
    EmptyState,
    EmptyStateActions,
    ExpandButton,
    ExpandSpacer,
    equalKeys,
    getRandomItemKey,
    Heading,
    Tree,
    type TreeData,
    TreeItem,
    TreeItemActions,
    TreeItemContent,
    toast,
    useDragAndDrop,
    useTreeData,
    useWatchTreeItems,
} from '@gitbook/spine-react';
import { useLiveRef } from '@gitbook/util-react-hooks';
import { assert, filterOutNullable } from '@gitbook/util-ts';

import { useSiteAudienceCommand } from '../../../AdaptiveContent';
import { SiteInsightsLinkUsageButton } from '../../../SiteInsights';
import { AddSubLinkButton } from '../AddSubLinkButton';
import { CustomizationContentLinkItem } from '../CustomizationContentLinkItem';
import { EditLinkButton } from '../EditLinkButton';
import { InvalidSpaceLinkHoverCard } from '../InvalidSpaceLink';
import { AddLinkDialog } from '../LinkDialog';
import { LinkStyleIndicator } from '../LinkStyleIndicator';
import { getItemsFactory, isLinkBroken } from '../util';

/** Soft limit for maximum no of header links & sub links */
const MAX_NO_OF_ITEMS = 6;
const MAX_NO_OF_SUB_LINKS = 8;

type AnyItem = CustomizationHeaderItem | CustomizationContentLink;

function checkIsLink<T extends AnyItem>(item: AnyItem): item is T & CustomizationContentLink {
    return item.to != null;
}

/**
 * Convert tree nodes to links.
 */
function fromTreeNodes(treeNodes: TreeData<AnyItem>['items']): CustomizationHeaderItem[] {
    const items = treeNodes.map(fromTreeNode);
    return items.map(toHeaderItem);
}

/**
 * Convert tree node to link.
 */
function fromTreeNode<T extends AnyItem>(node: TreeData<T>['items'][number]): T {
    let base = {
        title: node.value.title,
        links: (node.children ?? []).map((child) => {
            assert('to' in child.value, 'A dropdown cannot be a sublink.');
            return {
                title: child.value.title,
                to: child.value.to,
            };
        }),
    } as T;
    if ('style' in node.value) {
        base = { ...base, style: node.value.style };
    }
    if ('to' in node.value) {
        base = { ...base, to: node.value.to };
    }
    if ('condition' in node.value && node.value.condition) {
        base = { ...base, condition: node.value.condition };
    }
    return base;
}

/**
 * Hook to handle drag and drop for links.
 */
function useHeaderLinksDragAndDrop<T extends AnyItem>(
    treeRef: React.MutableRefObject<TreeData<T>>
) {
    return useDragAndDrop({
        ...treeRef.current.getDragAndDropOptions(),
        getItems: getItemsFactory(treeRef),
        // We could support copy but it's really a nice to have for this particular use case.
        getAllowedDropOperations: () => ['move'],
        getDropOperation(target, _types, allowedOperations) {
            const tree = treeRef.current;

            // Support only item drops, not root.
            if (target.type !== 'item') {
                return 'cancel';
            }

            // Pick the best allowed operation.
            const bestAllowedOperation = allowedOperations[0];

            // If we drag on the same item, cancel the operation.
            if (tree.draggedKeys.has(target.key)) {
                return 'cancel';
            }

            // If we want to drag just after an expanded item, cancel the operation.
            if (target.dropPosition === 'after' && tree.expandedKeys.has(target.key)) {
                return 'cancel';
            }

            const targetItem = tree.getItem(target.key);

            // We support only one dragged item.
            if (tree.draggedKeys.size !== 1) {
                return 'cancel';
            }

            const draggedKey = Array.from(tree.draggedKeys)[0];
            const draggedItem = tree.getItem(draggedKey);

            // If we can't find the item, cancel the operation.
            if (!targetItem || !draggedItem) {
                return 'cancel';
            }

            // Drag on an item.
            if (target.dropPosition === 'on') {
                // Do not allow to drag on sublinks.
                if (targetItem.parentKey) {
                    return 'cancel';
                }

                // Do not allow to drag if dragged item has some links.
                if (draggedItem.children && draggedItem.children.length > 0) {
                    return 'cancel';
                }

                // Do not allow to drag if the item is not a link.
                if (!('to' in draggedItem.value)) {
                    return 'cancel';
                }

                // Do not allow to drag if the target item is full.
                if (targetItem.children && targetItem.children.length >= MAX_NO_OF_SUB_LINKS) {
                    return 'cancel';
                }

                return bestAllowedOperation;
            }

            // Drag between sub-links.
            if (targetItem.parentKey) {
                // If the item has sublinks, don't allow to drag it.
                if (draggedItem.children && draggedItem.children.length > 0) {
                    return 'cancel';
                }

                // If the item is not a link, don't allow to drag it.
                if (!('to' in draggedItem.value)) {
                    return 'cancel';
                }

                const targetParent = tree.getItem(targetItem.parentKey);

                // If we drag from outside of the parent and it's full, don't allow it.
                if (
                    !equalKeys(draggedItem.parentKey, targetItem.parentKey) &&
                    targetParent?.children &&
                    targetParent.children.length >= MAX_NO_OF_SUB_LINKS
                ) {
                    return 'cancel';
                }
            }

            // Drag between top-level links.
            if (!targetItem.parentKey) {
                // If we drag a sublink, don't allow it if it's full.
                if (draggedItem.parentKey && tree.items.length >= MAX_NO_OF_ITEMS) {
                    return 'cancel';
                }
            }

            // If they are reordered.
            if (equalKeys(targetItem.parentKey, draggedItem.parentKey)) {
                const targetIndex = tree.getItemIndex(target.key);
                const draggedIndex = tree.getItemIndex(draggedKey);
                const resultIndex =
                    target.dropPosition === 'before' ? targetIndex - 1 : targetIndex + 1;

                // Don't allow to reorder if the position will be the same.
                if (resultIndex === draggedIndex) {
                    return 'cancel';
                }
            }

            return bestAllowedOperation;
        },
        onItemDrop: async (event) => {
            const tree = treeRef.current;
            switch (event.dropOperation) {
                case 'move': {
                    if (!event.isInternal) {
                        return;
                    }
                    const keys = (
                        await Promise.all(
                            event.items.map(async (item) => {
                                if (item.kind !== 'text') {
                                    return null;
                                }
                                if (!item.types.has('x-gitbook/item-key')) {
                                    return null;
                                }
                                const text = await item.getText('x-gitbook/item-key');
                                return text;
                            })
                        )
                    ).filter(filterOutNullable);
                    const key = keys[0];
                    if (!key) {
                        return;
                    }
                    const item = tree.getItem(key);
                    const targetItem = tree.getItem(event.target.key);
                    if (!item || !targetItem?.children) {
                        return;
                    }
                    tree.move(item.key, targetItem.key, targetItem.children.length);
                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
        onMove: (event) => {
            const tree = treeRef.current;
            switch (event.dropOperation) {
                case 'move': {
                    // We support only one dragged item.
                    if (event.keys.size !== 1) {
                        return;
                    }

                    const movedKey = Array.from(event.keys)[0];
                    const movedItem = tree.getItem(movedKey);
                    const targetItem = tree.getItem(event.target.key);
                    if (!targetItem || !movedItem) {
                        return;
                    }
                    const targetParent = tree.getParent(event.target.key);
                    const targetIndex = tree.getItemIndex(event.target.key);
                    if (targetIndex === -1) {
                        return;
                    }

                    const toParentKey = targetParent?.key ?? null;

                    // Move a top-level link into a sublink
                    if (!movedItem.parentKey && toParentKey) {
                        // If it's a dropdown we can't move it.
                        if (!checkIsLink(movedItem.value)) {
                            return;
                        }
                        if (movedItem.children && movedItem.children.length > 0) {
                            toast.info('Only one sublink level is allowed.');
                            return;
                        }
                        tree.update(movedKey, toContentLink(movedItem.value) as T);
                    }

                    // Move a sublink into a top-level link
                    if (!toParentKey && movedItem.parentKey && 'to' in movedItem.value) {
                        tree.update(movedKey, toHeaderItem(movedItem.value) as T);
                    }

                    const toIndex =
                        event.target.dropPosition === 'after' && movedItem.parentKey !== toParentKey
                            ? targetIndex + 1
                            : targetIndex;

                    tree.move(movedKey, toParentKey, toIndex);

                    return;
                }
                default:
                    throw new Error(`Unsupported drop operation: ${event.dropOperation}`);
            }
        },
    });
}

/**
 * Normalize header items to add the style property if it's missing.
 */
function normalizeHeaderItems(items: CustomizationHeaderItem[]) {
    return items.map(toHeaderItem);
}

/**
 * Convert a link to a content link.
 */
function toContentLink(link: CustomizationContentLink): CustomizationContentLink {
    return {
        title: link.title,
        to: link.to,
    };
}

function toHeaderItem(
    item: CustomizationHeaderItem | CustomizationContentLink
): CustomizationHeaderItem {
    return {
        title: item.title,
        to: 'to' in item ? item.to : null,
        style: 'style' in item && item.style ? item.style : 'link',
        links: 'links' in item && item.links ? item.links?.map(toContentLink) : [],
        ...(item && 'condition' in item && item.condition ? { condition: item.condition } : {}),
    };
}

export function HeaderNavigationEditor(props: {
    site: Site;
    organizationId: string;
    items: CustomizationHeaderItem[];
    onChange: (items: CustomizationHeaderItem[]) => void;
    siteSpaceIds: string[];
}) {
    const { site, organizationId } = props;
    const [initialItems] = React.useState(() => normalizeHeaderItems(props.items));
    const tree = useTreeData<AnyItem>({
        initialItems,
        // Generate a random key for each item because we don't have anything stable.
        getKey: getRandomItemKey,
        getChildren: (item) => {
            if ('links' in item && item.links) {
                return item.links;
            }
            return [];
        },
    });
    const treeRef = useLiveRef(tree);

    useWatchTreeItems(tree, (items) => {
        props.onChange(fromTreeNodes(items));
    });

    const { dragAndDropHooks } = useHeaderLinksDragAndDrop(treeRef);

    const addLinkButton = (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    kind="secondary"
                    label="Add item"
                    leadingIcon={Icon.Plus}
                    disabled={tree.items.length >= MAX_NO_OF_ITEMS}
                    tooltip={
                        tree.items.length >= MAX_NO_OF_ITEMS
                            ? {
                                  label: `You can't add more than ${MAX_NO_OF_ITEMS} items.`,
                              }
                            : undefined
                    }
                />
            </DialogTrigger>
            <AddLinkDialog
                title="Add item"
                onAdd={(newItem) => {
                    tree.append(null, {
                        ...newItem,
                        links: [],
                    });
                }}
                itemType="dropdown"
                siteSpaceIds={props.siteSpaceIds}
            />
        </DialogRoot>
    );

    return (
        <ActionableContainer>
            <Tree
                expandedKeys={tree.expandedKeys}
                onExpandedChange={tree.setExpandedKeys}
                aria-label="Links"
                items={tree.items}
                dragAndDropHooks={dragAndDropHooks}
                className="w-full"
                renderEmptyState={() => (
                    <EmptyState>
                        <Heading>No header links</Heading>
                        <Description>Create a navigation menu by adding links.</Description>
                        <EmptyStateActions>{addLinkButton}</EmptyStateActions>
                    </EmptyState>
                )}
            >
                {function renderItem(item) {
                    const isBrokenLink = isLinkBroken(item.value, props.siteSpaceIds);
                    return (
                        <TreeItem textValue={item.value.title}>
                            <TreeItemContent>
                                <TreeItemActions>
                                    <DragHandle />
                                    <AddSubLinkButton
                                        treeRef={treeRef}
                                        item={item}
                                        maxSubLinks={MAX_NO_OF_SUB_LINKS}
                                        siteSpaceIds={props.siteSpaceIds}
                                    />
                                    {item.children?.length ? <ExpandButton /> : <ExpandSpacer />}
                                </TreeItemActions>
                                {checkIsLink(item.value) ? (
                                    <CustomizationContentLinkItem
                                        contentLink={item.value}
                                        className="flex-1"
                                    />
                                ) : (
                                    <div className="flex-1 truncate">{item.value.title}</div>
                                )}
                                {isBrokenLink ? <InvalidSpaceLinkHoverCard /> : null}
                                {item.parentKey || isBrokenLink ? null : !item.value.to ? (
                                    <div className="flex items-center gap-1 text-ui-small">
                                        Menu <ChevronDown size={10} />
                                    </div>
                                ) : 'style' in item.value && item.value.style ? (
                                    <LinkStyleIndicator linkStyle={item.value.style} />
                                ) : null}
                                <TreeItemActions>
                                    {!isBrokenLink && item.value.to ? (
                                        <React.Suspense
                                            fallback={
                                                <ButtonSkeleton
                                                    size="small"
                                                    iconOnly
                                                    label="Usage"
                                                />
                                            }
                                        >
                                            <SiteInsightsLinkUsageButton
                                                size="small"
                                                iconOnly
                                                kind="blank"
                                                link={{
                                                    site,
                                                    organizationId,
                                                    contentRef: item.value.to,
                                                    position: SiteInsightsLinkPosition.Header,
                                                }}
                                            />
                                        </React.Suspense>
                                    ) : null}
                                    <EditLinkButton
                                        item={fromTreeNode(item)}
                                        itemType={item.parentKey ? 'link' : 'dropdown'}
                                        onChange={(newItem) => {
                                            treeRef.current.update(item.key, newItem);
                                        }}
                                        siteSpaceIds={props.siteSpaceIds}
                                    />
                                    <HeaderItemMoreButton
                                        organizationId={organizationId}
                                        site={site}
                                        treeRef={treeRef}
                                        item={item}
                                    />
                                </TreeItemActions>
                            </TreeItemContent>
                            {item.children && (
                                <Collection items={item.children}>{renderItem}</Collection>
                            )}
                        </TreeItem>
                    );
                }}
            </Tree>
            {tree.items.length > 0 && <Actions>{addLinkButton}</Actions>}
        </ActionableContainer>
    );
}

function HeaderItemMoreButton(props: {
    organizationId: string;
    site: Site;
    treeRef: React.MutableRefObject<TreeData<AnyItem>>;
    item: TreeData<AnyItem>['items'][number];
}) {
    const { organizationId, site, treeRef, item } = props;

    const audienceCommand = useSiteAudienceCommand({
        organizationId,
        site,
        target: 'header-link',
        condition:
            'condition' in item.value && item.value.condition ? item.value.condition : undefined,
        onChange: (condition) => {
            treeRef.current.update(item.key, { ...item.value, condition } as AnyItem);
        },
    });

    return (
        <>
            {'condition' in item.value && item.value.condition && audienceCommand ? (
                <Button
                    iconOnly
                    kind="blank"
                    size="small"
                    leadingIcon={Icon.Condition}
                    label="This header item is conditionally shown to visitors. Click to edit its condition."
                    onPress={() => {
                        audienceCommand?.onClickCondition();
                    }}
                />
            ) : null}
            <PaletteButton
                iconOnly
                kind="blank"
                size="small"
                label="More"
                leadingIcon={Icon.MoreHorizontal}
                palette={() =>
                    createPalette({
                        groups: [
                            ...(audienceCommand?.command && !item.parentKey
                                ? [
                                      {
                                          key: 'adaptive-content',
                                          commands: [audienceCommand.command],
                                      },
                                  ]
                                : []),
                            {
                                key: 'delete',
                                commands: [
                                    {
                                        key: 'delete',
                                        title: 'Delete',
                                        icon: Icon.Trashcan,
                                        trigger: () => {
                                            treeRef.current.remove(item.key);
                                        },
                                    },
                                ],
                            },
                        ],
                    })
                }
            />
            {audienceCommand?.dialogs}
        </>
    );
}
